import { queryOptions } from '@tanstack/react-query';
import { api, unwrap } from '@/api/client';
import { qk } from '@/api/queries';
import type { AiValues, CatalogSourceOut, CatalogSourcePatch, ConnectorOut, ConnectorPatch, DomainValues, McpGroup, RulesValues, Section, StorageValues, SyncStarted, User } from '@/api/types';

type SectionName = 'ai' | 'mcp' | 'storage' | 'domains' | 'rules';

/** GET /admin/settings/{section}: `values` is a dict in the schema; each section's shape is in src/api/types.ts. */
function sectionQuery<V>(section: SectionName) {
  return queryOptions({
    queryKey: qk.settings(section),
    queryFn: async (): Promise<Section<V>> => (await unwrap(api.GET('/admin/settings/{section}', { params: { path: { section } } }))) as unknown as Section<V>,
  });
}

export const aiQuery = sectionQuery<AiValues>('ai');
export const mcpQuery = sectionQuery<{ groups: McpGroup[] }>('mcp');
export const storageQuery = sectionQuery<StorageValues>('storage');
export const domainsQuery = sectionQuery<DomainValues>('domains');
export const rulesQuery = sectionQuery<RulesValues>('rules');

/** PATCH /admin/settings/{section}: changed fields only; a secret's string sets it, null clears it. */
export async function patchSection<V>(section: SectionName, body: Record<string, unknown>): Promise<Section<V>> {
  return (await unwrap(api.PATCH('/admin/settings/{section}', { params: { path: { section } }, body }))) as unknown as Section<V>;
}

export const usersQuery = queryOptions({
  queryKey: qk.users,
  queryFn: () => unwrap(api.GET('/admin/users', { params: { query: { size: 100 } } })),
  select: (p) => p.items as User[],
});

// ── «Источники Research» (P2-A01; CONNECTORS §4): live ──

/** GET /admin/settings/connectors: the research connectors, by kind (Admin). */
export const connectorsQuery = queryOptions({
  queryKey: qk.settings('connectors'),
  queryFn: async (): Promise<ConnectorOut[]> => (await unwrap(api.GET('/admin/settings/connectors'))) as unknown as ConnectorOut[],
});

/** PATCH /admin/settings/connectors/{kind}: enabled, limit overrides, credentials (a string sets, null clears). */
export async function patchConnector(kind: string, body: ConnectorPatch): Promise<ConnectorOut> {
  return (await unwrap(api.PATCH('/admin/settings/connectors/{kind}', { params: { path: { kind } }, body }))) as unknown as ConnectorOut;
}

// ── «Каталоги дизайна» (P3-D03) ──

export const catalogSourcesQuery = queryOptions({
  queryKey: qk.catalogSources,
  queryFn: (): Promise<CatalogSourceOut[]> => unwrap(api.GET('/admin/catalog-sources')),
});

export function patchCatalogSource(id: string, body: CatalogSourcePatch): Promise<CatalogSourceOut> {
  return unwrap(api.PATCH('/admin/catalog-sources/{source_id}', { params: { path: { source_id: id } }, body }));
}

/** POST /admin/catalog-sources/{id}/sync: an incremental sync job (202). */
export function syncCatalogSource(id: string): Promise<SyncStarted> {
  return unwrap(api.POST('/admin/catalog-sources/{source_id}/sync', { params: { path: { source_id: id } } }));
}
